import React from 'react';
import { IoChevronBackOutline, IoPrintSharp } from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router-dom';

const Receipt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const transaction = location.state?.transaction;
  const transactions = location.state?.transactions;
  const businessData = location.state?.businessData;

  const isMultipleTransactions = Array.isArray(transactions) && transactions.length > 0;
  const transactionsToRender = isMultipleTransactions
    ? transactions
    : transaction
    ? [transaction]
    : [];

  const getTableHeaders = () => {
    if (transactionsToRender.length === 0) return [];

    const firstTransaction = transactionsToRender[0];

    if (firstTransaction.businessDetails && firstTransaction.planDetails) {
      return [
        { key: 'id', label: 'Serial No.' },
        { key: 'businessName', label: 'Business Name' },
        { key: 'email', label: 'Email' },
        { key: 'subscriptionType', label: 'Subscription Type' },
        { key: 'country', label: 'Country' },
        { key: 'status', label: 'Status' },
        { key: 'price', label: 'Price' },
        { key: 'duration', label: 'Duration' },
      ];
    } else if (firstTransaction.type && firstTransaction.card) {
      return [
        { key: 'description', label: 'Description' },
        { key: 'id', label: 'Transaction ID' },
        { key: 'type', label: 'Type' },
        { key: 'card', label: 'Card' },
        { key: 'date', label: 'Date' },
        { key: 'amount', label: 'Amount' },
      ];
    } else {
      return [
        { key: 'description', label: 'Description' },
        { key: 'price', label: 'Cost per unit' },
        { key: 'quantity', label: 'QTY' },
        { key: 'total', label: 'Total' },
      ];
    }
  };

  const tableHeaders = location.state?.headers || getTableHeaders();

  const generateInvoiceItems = () => {
    if (transactionsToRender.length === 0) return [];

    return transactionsToRender.map(transaction => {
      if (transaction.businessDetails && transaction.planDetails) {
        return {
          id: transaction._id || transaction.id || 1,
          businessName: transaction.businessDetails?.name || '-',
          email: transaction.businessDetails?.customEmail || '-',
          subscriptionType: transaction.planDetails?.name || '-',
          country: transaction.businessDetails?.country || '-',
          status: transaction.status || '-',
          price: transaction.planDetails?.price || '-',
          duration: transaction.planDetails?.duration || 'Monthly' | '-',
        };
      } else {
        const price = transaction.price || transaction.amount?.replace(/[^0-9.-]+/g, '') || '0.00';
        const quantity = transaction.quantity || '1';
        const total =
          transaction.total || (parseFloat(price) * parseFloat(quantity)).toFixed(2) || '0.00';

        return {
          id: transaction.id || 1,
          description: transaction.description || '-',
          type: transaction.type || '-',
          card: transaction.card || '-',
          date: transaction.date || '-',
          amount: transaction.amount || '-',
          price: `$${price}`,
          quantity: quantity,
          total: `$${total}`,
        };
      }
    });
  };

  const firstTransaction = transactionsToRender.length > 0 ? transactionsToRender[0] : null;
  const invoice = {
    from: {
      name: 'QoutR',
      address: 'Address - CITY ZIP Code',
      contact: 'Phone - Email-address',
    },
    to: {
      name: businessData?.name || 'Name',
      address: businessData?.location || 'Street address',
      city: businessData?.other_location?.join(', ') || 'City, ST ZIP Code',
      email: businessData?.representatives?.[0]?.email || '-',
      contact: businessData?.representatives?.[0]?.phoneNumber || '-',
    },
    receiptNumber: firstTransaction?.id?.substring(0, 8) || '100',
    date: firstTransaction?.date || 'MM/DD/YYYY',
    items: generateInvoiceItems(),
  };

  const calculateSubtotal = () => {
    if (transactionsToRender.length === 0) return 0;

    return transactionsToRender.reduce((total, transaction) => {
      if (transaction.planDetails && transaction.planDetails.price) {
        return total + (parseFloat(transaction.planDetails.price) || 0);
      } else if (transaction.amount) {
        return total + (parseFloat(transaction.amount.replace(/[^0-9.-]+/g, '')) || 0);
      } else if (transaction.total) {
        return total + (parseFloat(transaction.total.replace(/[^0-9.-]+/g, '')) || 0);
      } else if (transaction.price) {
        return total + (parseFloat(transaction.price) || 0);
      }
      return total;
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const discount = 0;
  const taxRate = 0;
  const tax = subtotal * (taxRate / 100);
  const totalPaid = subtotal - discount + tax;

  const handlePrint = () => {
    const printContent = document.getElementById('printable').innerHTML;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(invoice, null, 2)], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    element.download = 'receipt.json';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getCellValue = (item, key) => {
    if (key === 'status' && item[key]) {
      return (
        <span
          className={`rounded px-2.5 py-1 text-sm font-medium 
          ${
            item[key] === 'active'
              ? 'bg-[#E7F7EF] text-[#00A36C]'
              : item[key] === 'inactive'
              ? 'bg-[#FEEFEF] text-[#E74C3C]'
              : 'bg-gray-300 text-gray-600'
          }`}>
          {item[key]}
        </span>
      );
    }

    if (key === 'amount' && item[key] && typeof item[key] === 'string') {
      return (
        <span className={`${item[key].startsWith('-') ? 'text-red-500' : 'text-green-500'}`}>
          {item[key]}
        </span>
      );
    }

    return item[key] || '-';
  };

  return (
    <div className="sm:ml-64 w-[80%] bg-[#F4F5F6] p-3">
      <div className="mb-2">
        <p className="font-sans font-normal text-sm text-[#191B1C]">Business transactions</p>
      </div>

      <div className="p-6 pb-8 bg-white">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-10">
            <button
              onClick={handleBack}
              className="p-2 transition-colors bg-gray-100 rounded-full hover:bg-gray-200">
              <IoChevronBackOutline className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-semibold">
              {isMultipleTransactions ? `Receipt (${transactions?.length} items)` : 'Receipt'}
            </h1>
          </div>

          <div
            id="printable"
            className="w-full max-w-3xl p-8 mx-auto bg-white border border-gray-300 rounded-lg shadow">
            {/* Receipt Header */}
            <div className="flex items-start justify-between pb-6 mb-8 border-b">
              <div className="w-1/2" />
              <div className="w-1/2 text-right">
                <h1 className="mb-2 text-2xl font-bold text-gray-700">RECEIPT</h1>
                <p className="font-medium text-gray-600">{invoice.from.name}</p>
                <p className="text-sm text-gray-600">{invoice.from.address}</p>
                <p className="text-sm text-gray-600">{invoice.from.contact}</p>
              </div>
            </div>

            {/* Billing Information */}
            <div className="flex justify-between mb-8">
              <div className="w-1/2">
                <h2 className="mb-2 font-medium text-gray-600">BILLED TO</h2>
                <p className="text-gray-700">{invoice.to.name}</p>
                <p className="text-sm text-gray-600">{invoice.to.address}</p>
                <p className="text-sm text-gray-600">{invoice.to.city}</p>
                <p className="text-sm text-gray-600">{invoice.to.email}</p>
                <p className="text-sm text-gray-600">{invoice.to.contact}</p>
              </div>
              <div className="w-1/2 text-right">
                <div className="mb-4">
                  <h2 className="mb-2 font-medium text-gray-600">RECEIPT NUMBER</h2>
                  <p className="text-gray-700">{invoice.receiptNumber}</p>
                </div>
                <div>
                  <h2 className="mb-2 font-medium text-gray-600">DATE</h2>
                  <p className="text-gray-700">{invoice.date}</p>
                </div>
              </div>
            </div>

            {/* Table */}
            {invoice.items.length > 0 && (
              <div className="relative mb-6 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300">
                      {tableHeaders.map(header => (
                        <th key={header.key} scope="col" className="py-3 font-medium text-gray-600">
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => (
                      <tr key={`${item.id}-${index}`} className="border-b border-gray-200">
                        {tableHeaders.map(header => (
                          <td key={`${item.id}-${header.key}`} className="py-4 text-gray-700">
                            {getCellValue(item, header.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals Section */}
            <div className="flex justify-end mb-8">
              <div className="w-1/3 text-right">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">SUBTOTAL</span>
                  <span className="text-gray-700">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">DISCOUNT</span>
                  <span className="text-gray-700">${discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">TAX (TAX RATE %)</span>
                  <span className="text-gray-700">${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span className="text-gray-600">TOTAL PAID</span>
                  <span className="text-gray-700">${totalPaid.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <button
                onClick={handlePrint}
                className="flex items-center bg-gray-100 text-sm text-black px-4 py-2.5 mx-2 rounded hover:bg-gray-200">
                <IoPrintSharp className="mr-2" />
                Print
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center bg-black text-sm text-white px-6 py-2.5 mx-2 rounded hover:bg-gray-800">
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Receipt;
